/**
 * 画像最適化ユーティリティ
 * クイズ用画像の圧縮・リサイズを行う
 */
export const imageOptimizer = {
  /**
   * 画像を圧縮・最適化する
   * @param {File} file - 元画像ファイル
   * @param {Object} options - 圧縮オプション
   * @returns {Promise<File>} 最適化されたファイル
   */
  compressImage: async (file, options = {}) => {
    const {
      maxWidth = 800,        // 最大幅
      maxHeight = 600,       // 最大高さ
      quality = 0.8,         // 品質 (0.1-1.0)
      format = 'webp'        // 出力形式
    } = options;

    return new Promise((resolve, reject) => {
      // Canvasを使用した画像処理
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        try {
          // アスペクト比を保持してリサイズ計算
          const { width, height } = calculateOptimalSize(
            img.width, 
            img.height, 
            maxWidth, 
            maxHeight
          );

          // Canvasサイズ設定
          canvas.width = width;
          canvas.height = height;

          // 高品質描画設定
          ctx.imageSmoothingEnabled = true;
          ctx.imageSmoothingQuality = 'high';

          // 画像を描画
          ctx.drawImage(img, 0, 0, width, height);

          // 最適化された画像をBlobとして出力
          canvas.toBlob(
            (blob) => {
              if (blob) {
                // BlobをFileオブジェクトに変換
                const optimizedFile = new File(
                  [blob], 
                  `optimized_${file.name.replace(/\.[^/.]+$/, '')}.${format}`,
                  { type: `image/${format}` }
                );
                resolve(optimizedFile);
              } else {
                reject(new Error('画像の圧縮に失敗しました'));
              }
            },
            `image/${format}`,
            quality
          );
        } catch (error) {
          reject(error);
        }
      };

      img.onerror = () => reject(new Error('画像の読み込みに失敗しました'));
      img.src = URL.createObjectURL(file);
    });
  },

  /**
   * 画像のプレビュー用サムネイルを生成
   * @param {File} file - 元画像ファイル
   * @returns {Promise<string>} データURL
   */
  generateThumbnail: async (file) => {
    return new Promise((resolve, reject) => {
      const canvas = document.createElement('canvas');
      const ctx = canvas.getContext('2d');
      const img = new Image();

      img.onload = () => {
        const thumbnailSize = 150;
        canvas.width = thumbnailSize;
        canvas.height = thumbnailSize;

        // 正方形にクロップして描画
        const size = Math.min(img.width, img.height);
        const sx = (img.width - size) / 2;
        const sy = (img.height - size) / 2;

        ctx.drawImage(img, sx, sy, size, size, 0, 0, thumbnailSize, thumbnailSize);
        resolve(canvas.toDataURL('image/jpeg', 0.7));
      };

      img.onerror = () => reject(new Error('サムネイル生成に失敗しました'));
      img.src = URL.createObjectURL(file);
    });
  }
};

/**
 * アスペクト比を保持した最適なサイズを計算
 */
function calculateOptimalSize(originalWidth, originalHeight, maxWidth, maxHeight) {
  let width = originalWidth;
  let height = originalHeight;

  // 最大サイズを超える場合のリサイズ
  if (width > maxWidth) {
    height = (height * maxWidth) / width;
    width = maxWidth;
  }

  if (height > maxHeight) {
    width = (width * maxHeight) / height;
    height = maxHeight;
  }

  return { width: Math.round(width), height: Math.round(height) };
} 